import BaseController from "./base_controller"
import { t } from "@/utils/i18n"

// Connects to data-controller="delete-confirmation"
export default class extends BaseController {
  static targets = ["modal", "confirmButton", "cancelButton", "itemName"]
  static values = { url: String, method: String, itemName: String }

  declare readonly modalTarget: HTMLDialogElement
  declare readonly confirmButtonTarget: HTMLButtonElement
  declare readonly cancelButtonTarget: HTMLButtonElement
  declare readonly itemNameTarget: HTMLElement
  declare readonly hasModalTarget: boolean
  declare readonly hasConfirmButtonTarget: boolean
  declare readonly hasCancelButtonTarget: boolean
  declare readonly hasItemNameTarget: boolean

  declare urlValue: string
  declare methodValue: string
  declare itemNameValue: string

  private boundHandleModalClick: ((event: MouseEvent) => void) | null = null

  connect(): void {
    super.connect()
    this.debug('DeleteConfirmationController connected')
    this.bindEvents()
  }

  disconnect(): void {
    super.disconnect()
    this.hideModal()
    this.cleanupEvents()
  }

  private bindEvents(): void {
    try {
      // Store bound functions for cleanup
      this.boundHandleModalClick = this.handleModalClick.bind(this)

      // Click outside modal to close
      if (this.hasModalTarget) {
        this.modalTarget.addEventListener('click', this.boundHandleModalClick)
      }

    } catch (error) {
      this.handleError(error, 'bindEvents')
    }
  }

  private cleanupEvents(): void {
    try {
      // Remove modal event listeners
      if (this.boundHandleModalClick && this.hasModalTarget) {
        this.modalTarget.removeEventListener('click', this.boundHandleModalClick)
        this.boundHandleModalClick = null
      }

    } catch (error) {
      this.debug('Event cleanup error (non-critical)', error)
    }
  }

  // Show modal with confirmation
  show(event: MouseEvent): void {
    this.debug('DeleteConfirmationController show called')
    try {
      event.preventDefault()

      if (!this.hasModalTarget) {
        this.handleError(new Error('Modal target not found'), 'show')
        return
      }

      // Get values from button data attributes
      const button = event.currentTarget as HTMLElement
      this.urlValue = button.dataset.deleteConfirmationUrlValue || ''
      this.methodValue = button.dataset.deleteConfirmationMethodValue || 'DELETE'
      const title = button.dataset.deleteConfirmationTitleValue
      const message = button.dataset.deleteConfirmationMessageValue

      // Update modal content
      if (title) {
        const titleElement = this.modalTarget.querySelector('#modal-title')
        if (titleElement) titleElement.textContent = title
      }

      if (message) {
        const messageElement = this.modalTarget.querySelector('.delete-message')
        if (messageElement) messageElement.textContent = message
      }

      this.showModal()

    } catch (error) {
      this.handleError(error, 'show')
    }
  }

  // Handle confirmation
  confirm(event: MouseEvent): void {
    try {
      event.preventDefault()

      // Use Values directly instead of undefined properties
      if (!this.urlValue) {
        this.showErrorMessage(t('delete_missing_url'))
        return
      }

      // Show loading state
      if (this.hasConfirmButtonTarget) {
        this.showLoadingState(this.confirmButtonTarget, t('deleting'))
      }

      // Create and submit Turbo form using Values
      const form = this.createTurboForm(this.urlValue, (this.methodValue || 'DELETE').toUpperCase())

      // Report the outcome only once the server has answered — announcing
      // success before the request completes made a failed delete look fine.
      form.addEventListener('turbo:submit-end', (event: Event) => {
        this.finishDelete((event as CustomEvent).detail?.success !== false)
      }, { once: true })

      this.submitTurboForm(form)

    } catch (error) {
      this.handleError(error, 'confirm')
      this.hideModal()
      if (this.hasConfirmButtonTarget) {
        this.hideLoadingState(this.confirmButtonTarget)
      }
    }
  }

  private finishDelete(success: boolean): void {
    if (this.hasConfirmButtonTarget) {
      this.hideLoadingState(this.confirmButtonTarget)
    }
    this.hideModal()

    if (!success) {
      this.showErrorMessage(t('generic_error'))
      return
    }

    this.showSuccessMessage(t('delete_success'))
    this.handleSuccessfulDelete()
  }

  // Handle cancellation
  cancel(event: MouseEvent): void {
    try {
      event.preventDefault()
      this.hideModal()
      this.showInfoMessage(t('delete_cancelled'))
    } catch (error) {
      this.handleError(error, 'cancel')
    }
  }

  // Modal visibility methods
  private showModal(): void {
    try {
      if (this.hasModalTarget) {
        // Use native dialog API for <dialog> element
        this.modalTarget.showModal()

        // Focus on confirm button for accessibility
        if (this.hasConfirmButtonTarget) {
          setTimeout(() => {
            this.confirmButtonTarget.focus()
          }, 100)
        }
      } else {
        this.handleError(new Error('No modalTarget available'), 'showModal')
      }

    } catch (error) {
      this.handleError(error, 'showModal')
    }
  }

  private hideModal(): void {
    try {
      if (this.hasModalTarget) {
        // Use native dialog API for <dialog> element
        this.modalTarget.close()
      }

    } catch (error) {
      this.handleError(error, 'hideModal')
    }
  }

  // Event handlers
  private handleModalClick(event: MouseEvent): void {
    try {
      // Close modal if clicking on backdrop
      if (event.target === this.modalTarget) {
        this.hideModal()
      }
    } catch (error) {
      this.debug('Modal click handler error (non-critical)', error)
    }
  }

  // Success handling
  private handleSuccessfulDelete(): void {
    // Give the success toast a moment to be read, then re-render the page.
    setTimeout(() => {
      try {
        Turbo.visit(window.location.pathname, { action: "replace" })
      } catch {
        window.location.reload()
      }
    }, 1000)
  }

  // Value change handlers
  urlValueChanged(): void {
    // URL value changed
  }

  methodValueChanged(): void {
    // Method value changed
  }
}
